import { downloadAccountDataBundle } from './accountDataSyncApi';
import { uploadAccountDataBundle } from './accountDataSyncApi';
import { getAccountSyncKey } from './syncKeyStore';
import { mutableBundleForSourceAuthSave } from './accountSourceAuthBundle.ts';
import { snapshotAccountDataBundle } from './localAccountData.ts';
import { setAuthWithKey } from '@/db/credentials';
import { PassphraseSession } from '@/lib/crypto';
import type { SourceAuth } from '@/sources/types';
import type { AccountDataBundle } from './accountDataSync';

export async function loadAccountSyncedSourceAuth(
  sourceId: string,
  token: string | undefined,
  accountIdentity: string | undefined,
): Promise<SourceAuth | undefined> {
  if (!token || !accountIdentity) return undefined;
  try {
    const syncKey = await getAccountSyncKey(token);
    PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
    const bundle = await downloadAccountDataBundle(token, syncKey);
    return bundle?.sourceAuth.find((entry) => entry.source === sourceId)?.auth;
  } catch {
    return undefined;
  }
}

export async function saveAccountSyncedSourceAuth(
  sourceId: string,
  auth: SourceAuth,
  token: string,
  accountIdentity: string,
  getCurrentAccountIdentity: () => string | undefined,
  opts: { username?: string; mode?: 'anonymous' } = {},
): Promise<void> {
  const syncKey = await getAccountSyncKey(token);
  PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
  const bundle = await mutableBundleForSourceAuthSave(
    () => downloadAccountDataBundle(token, syncKey),
    snapshotAccountDataBundle,
  );
  bundle.exportedAt = Date.now();
  bundle.sourceAuth = bundle.sourceAuth.filter((entry) => entry.source !== sourceId);
  const entry: AccountDataBundle['sourceAuth'][number] = { source: sourceId, auth };
  if (opts.username !== undefined) entry.username = opts.username;
  if (opts.mode !== undefined) entry.mode = opts.mode;
  bundle.sourceAuth.push(entry);
  await uploadAccountDataBundle(token, bundle, syncKey);
  const saved = await setAuthWithKey(
    sourceId,
    auth,
    syncKey,
    { username: opts.username },
    () => getCurrentAccountIdentity() === accountIdentity,
  );
  if (!saved) throw new Error('Account changed while saving source cookies.');
}

export async function restoreAccountSyncedSourceAuthToLocal(
  sourceId: string,
  token: string,
  accountIdentity: string,
  getCurrentAccountIdentity: () => string | undefined,
): Promise<boolean> {
  const syncKey = await getAccountSyncKey(token);
  PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
  const bundle = await downloadAccountDataBundle(token, syncKey);
  const entry = bundle?.sourceAuth.find((candidate) => candidate.source === sourceId);
  if (!entry) return false;
  const saved = await setAuthWithKey(
    sourceId,
    entry.auth,
    syncKey,
    { username: entry.username },
    () => getCurrentAccountIdentity() === accountIdentity,
  );
  if (!saved) throw new Error('Account changed while restoring source cookies.');
  return true;
}
